/**
 * Mock Supply Chain exception data — full agentic resolution flows for the
 * Supply Chain Command Center cases (logistics + procurement).
 *
 * Each case carries: signal → agent traces → evidence → decision → action → HITL → outcome.
 * Synthetic data only — no real companies referenced.
 *
 * Agent roster (supply chain):
 *   Logistics:   Supply Chain Planner, Logistics Systems Agent, Inventory Agent,
 *                SLA & Contract Agent, Cost & Risk Analyst, Decision Agent, Execution Agent
 *   Procurement: Procurement Planner, PO Systems Agent, Contract Specialist,
 *                Vendor Evaluation Analyst, Material Planning Agent, Decision Agent,
 *                Supplier Communication Agent
 */
import {
  ExceptionCase,
  TimelineEvent,
  EntityNode,
  EntityEdge,
  Signal,
} from './mockExceptionData';

// ─── Signals ─────────────────────────────────────────────────────────────────

export const scMockSignals: Signal[] = [
  {
    signal_id: 'sig-sc-118',
    source: 'carrier_edi_214',
    domain: 'supply_chain',
    workflow_type: 'customer_order_risk',
    severity: 'critical',
    title: 'Third missed pickup — SHP-7720 to Pacific Components',
    description: 'RoadLink Freight missed pickup at Memphis DC for the 3rd day. Revised delivery Jun 21 vs SLA Jun 17.',
    entity_type: 'shipment',
    entity_id: 'SHP-7720',
    attributes: { category: 'logistics', carrier: 'RoadLink Freight', missed_pickups: 3, revised_eta: '2026-06-21' },
    detected_at: '2026-06-17T06:40:00Z',
  },
  {
    signal_id: 'sig-sc-124',
    source: 'wms_allocation_scan',
    domain: 'supply_chain',
    workflow_type: 'customer_order_risk',
    severity: 'high',
    title: 'Allocation shortfall — SO-44187 for NorthStar Logistics',
    description: 'Dallas DC cannot allocate 1,200 units of SKU SK-220. Order is 5 days past promise date; 7-day grace ends Jun 17.',
    entity_type: 'customer_order',
    entity_id: 'SO-44187',
    attributes: { category: 'logistics', sku: 'SK-220', short_units: 1200, grace_ends: '2026-06-17' },
    detected_at: '2026-06-15T08:05:00Z',
  },
  {
    signal_id: 'sig-sc-137',
    source: 'erp_goods_receipt',
    domain: 'supply_chain',
    workflow_type: 'procurement_exception',
    severity: 'medium',
    title: 'Short receipt — PO-88213 from Apex Packaging',
    description: 'GR-71840 posted 7,600 of 10,000 cartons (24% short) against ASN-55102 which declared 10,000.',
    entity_type: 'purchase_order',
    entity_id: 'PO-88213',
    attributes: { category: 'procurement', ordered: 10000, received: 7600, asn_qty: 10000 },
    detected_at: '2026-06-15T10:20:00Z',
  },
  {
    signal_id: 'sig-sc-142',
    source: 'supplier_po_ack',
    domain: 'supply_chain',
    workflow_type: 'procurement_exception',
    severity: 'high',
    title: 'PO acknowledgment mismatch — PO-88342 from Meridian Metals',
    description: 'Supplier acknowledged at $10.58/kg vs PO $9.80/kg (+8% "energy surcharge") and pushed delivery by 7 days.',
    entity_type: 'purchase_order',
    entity_id: 'PO-88342',
    attributes: { category: 'procurement', po_price: 9.8, ack_price: 10.58, delivery_slip_days: 7 },
    detected_at: '2026-06-16T09:30:00Z',
  },
];

// ─── Cases ───────────────────────────────────────────────────────────────────

export const scMockCases: ExceptionCase[] = [
  {
    case_id: 'SC-2024-118',
    signal_id: 'sig-sc-118',
    domain: 'supply_chain',
    workflow_type: 'customer_order_risk',
    status: 'approval_needed',
    severity: 'critical',
    title: 'SC-2024-118 late shipment — Pacific Components line-down risk',
    description:
      'SO-44120 ($820K) shipment SHP-7720 has missed pickup 3 days running. Customer safety stock on control modules runs out Jun 19 EOD; line-down chargeback applies from Jun 20.',
    entity_type: 'shipment',
    entity_id: 'SHP-7720',
    attributes: {
      category: 'logistics',
      customer: 'Pacific Components',
      order_id: 'SO-44120',
      order_value: 820000,
      sla_delivery: '2026-06-17',
      revised_eta: '2026-06-21',
      recommended_option: 'team_driver_expedite',
      expedite_cost: 14600,
      exposure_avoided: 91800,
    },
    assigned_team_id: 'logistics_team',
    owner_id: 'logistics_manager_01',
    created_at: '2026-06-17T06:41:00Z',
    updated_at: '2026-06-17T07:02:00Z',
  },
  {
    case_id: 'SC-2024-124',
    signal_id: 'sig-sc-124',
    domain: 'supply_chain',
    workflow_type: 'customer_order_risk',
    status: 'resolved',
    severity: 'high',
    title: 'SC-2024-124 SLA breach risk — NorthStar Logistics backorder',
    description:
      'SO-44187 ($410K) was 5 days past promise with 2 days of grace left. Fulfilled from Reno and Columbus DCs before breach; no penalty, order retained.',
    entity_type: 'customer_order',
    entity_id: 'SO-44187',
    attributes: {
      category: 'logistics',
      customer: 'NorthStar Logistics',
      order_value: 410000,
      sku: 'SK-220',
      units: 1200,
      freight_premium: 6800,
      resolution: 'multi_node_fulfillment',
    },
    assigned_team_id: 'logistics_team',
    created_at: '2026-06-15T08:06:00Z',
    updated_at: '2026-06-16T13:10:00Z',
  },
  {
    case_id: 'SC-2024-137',
    signal_id: 'sig-sc-137',
    domain: 'supply_chain',
    workflow_type: 'procurement_exception',
    status: 'waiting_on_owner',
    severity: 'medium',
    title: 'SC-2024-137 short shipment — Apex Packaging PO-88213',
    description:
      '2,400 of 10,000 cartons short. Invoice billed full quantity, so the 3-way match fails. Backorder requested under the 48h contract cure period; spot-buy contingency staged.',
    entity_type: 'purchase_order',
    entity_id: 'PO-88213',
    attributes: {
      category: 'procurement',
      supplier: 'Apex Packaging',
      po_value: 275000,
      short_units: 2400,
      short_value: 66000,
      cure_deadline: '2026-06-17T14:00:00Z',
      invoice_hold: 66000,
    },
    assigned_team_id: 'procurement_team',
    owner_id: 'Apex Packaging (supplier)',
    created_at: '2026-06-15T10:21:00Z',
    updated_at: '2026-06-15T13:20:00Z',
  },
  {
    case_id: 'SC-2024-142',
    signal_id: 'sig-sc-142',
    domain: 'supply_chain',
    workflow_type: 'procurement_exception',
    status: 'approval_needed',
    severity: 'high',
    title: 'SC-2024-142 PO mismatch — Meridian Metals surcharge on PO-88342',
    description:
      'Supplier acknowledgment adds an 8% surcharge ($14K) and slips delivery 7 days. Contract fixes pricing through Dec 2026 and requires 60-day notice for index-linked adjustments.',
    entity_type: 'purchase_order',
    entity_id: 'PO-88342',
    attributes: {
      category: 'procurement',
      supplier: 'Meridian Metals',
      po_value: 176400,
      ack_value: 190440,
      variance: 14040,
      variance_percent: 8,
      delivery_slip_days: 7,
    },
    assigned_team_id: 'procurement_team',
    owner_id: 'category_manager_metals',
    created_at: '2026-06-16T09:31:00Z',
    updated_at: '2026-06-16T09:58:00Z',
  },
];

// ─── Timelines ───────────────────────────────────────────────────────────────

export const scMockTimeline: Record<string, TimelineEvent[]> = {
  // ── Logistics: late shipment, recommendation awaiting human approval ──────
  'SC-2024-118': [
    { type: 'case_created', timestamp: '2026-06-17T06:41:00Z', data: { status: 'detected', source: 'carrier_edi_214' } },
    {
      type: 'agent_trace',
      timestamp: '2026-06-17T06:42:00Z',
      data: {
        agent_name: 'Supply Chain Planner',
        operation: 'plan',
        output_summary:
          'Late outbound shipment on a strategic account. Plan: (1) pull order + SLA, (2) carrier status and alternatives, (3) inventory at other nodes, (4) contract penalties, (5) cost/risk comparison, (6) decision with approval routing.',
      },
    },
    {
      type: 'agent_trace',
      timestamp: '2026-06-17T06:43:00Z',
      data: {
        agent_name: 'Logistics Systems Agent',
        operation: 'get_customer_order',
        output_summary:
          'SO-44120: Pacific Components, 3 lines, $820,000. Line 1 = 1,600 control modules ($328K, line-critical). SLA delivery Jun 17 to Fremont, CA plant.',
      },
    },
    {
      type: 'agent_trace',
      timestamp: '2026-06-17T06:44:00Z',
      data: {
        agent_name: 'Logistics Systems Agent',
        operation: 'get_shipment_status',
        output_summary:
          'SHP-7720 tendered to RoadLink Freight (FTL). Missed pickups Jun 14, 15, 16 — reason code "capacity shortage". RoadLink revised pickup Jun 18 → delivery Jun 21 (4 days late).',
      },
    },
    {
      type: 'agent_trace',
      timestamp: '2026-06-17T06:46:00Z',
      data: {
        agent_name: 'Inventory Agent',
        operation: 'check_network_inventory',
        output_summary:
          'Memphis DC holds the full order (staged at dock 14). Reno DC has 560 control modules (35% of line 1) — insufficient to cover alone. No in-transit stock to West Coast.',
      },
    },
    {
      type: 'agent_trace',
      timestamp: '2026-06-17T06:48:00Z',
      data: {
        agent_name: 'SLA & Contract Agent',
        operation: 'rag_search',
        output_summary:
          'MSA-PC-2023 §7.2: late-delivery penalty 1%/day of late order value (cap 10%). §7.5 line-down clause: $45,000/day chargeback if line-critical parts cause a production stop. Customer safety stock covers through Jun 19 EOD.',
      },
    },
    {
      type: 'evidence_collected',
      timestamp: '2026-06-17T06:48:30Z',
      data: {
        title: 'Customer contract — penalty and line-down clauses',
        evidence_type: 'document',
        content: 'MSA-PC-2023 §7.2 (1%/day late penalty, 10% cap) and §7.5 ($45K/day line-down chargeback).',
      },
    },
    {
      type: 'agent_trace',
      timestamp: '2026-06-17T06:50:00Z',
      data: {
        agent_name: 'Logistics Systems Agent',
        operation: 'get_alternate_carriers',
        output_summary:
          'SwiftLane Expedite: team-driver FTL, pickup today 16:00, delivery Jun 19 10:00, $14,600. AirBridge Cargo: next-flight-out for line 1 only, delivery Jun 18, $18,900 (+$9,400 ground for lines 2–3).',
      },
    },
    {
      type: 'evidence_collected',
      timestamp: '2026-06-17T06:50:30Z',
      data: {
        title: 'Carrier tender history — RoadLink service failure',
        evidence_type: 'api_response',
        content: '3 consecutive missed pickups on an accepted tender. Carrier agreement CA-RL-2025 §4.3 allows a $1,500 per-failure service claim ($4,500).',
      },
    },
    {
      type: 'agent_trace',
      timestamp: '2026-06-17T06:54:00Z',
      data: {
        agent_name: 'Cost & Risk Analyst',
        operation: 'compare_options',
        output_summary:
          'A) Wait for RoadLink: 4d late ($32,800) + 2 line-down days ($90,000) = $122,800. B) Team-driver expedite: $14,600 freight + 2d late ($16,400), no line-down = $31,000. C) Air + ground split: $28,300 freight + $13,100 penalties = $41,400. B is lowest total cost.',
      },
    },
    {
      type: 'evidence_collected',
      timestamp: '2026-06-17T06:54:30Z',
      data: {
        title: 'Option comparison',
        evidence_type: 'analysis',
        content: 'Option B avoids $91,800 vs. waiting and $10,400 vs. air split. Arrival Jun 19 10:00 beats safety-stock run-out (Jun 19 EOD) by ~14h.',
      },
    },
    {
      type: 'agent_trace',
      timestamp: '2026-06-17T06:57:00Z',
      data: {
        agent_name: 'Decision Agent',
        operation: 'decide',
        output_summary:
          'Recommend Option B (confidence 0.88). Guardrail: $14,600 exceeds the $10,000 delegated freight authority, so this needs Logistics Manager approval. Also file a $4,500 RoadLink service claim and send the customer a revised ETA.',
      },
    },
    {
      type: 'decision',
      timestamp: '2026-06-17T06:57:30Z',
      data: {
        action: 'expedite_team_driver',
        decision_source: 'agent',
        reasoning: 'Lowest total cost ($31.0K vs $122.8K). Prevents line-down on a strategic account. Spend above delegated authority, so routed to a human.',
        owner: 'Decision Agent',
      },
    },
    { type: 'action', timestamp: '2026-06-17T06:58:00Z', data: { action_type: 'book_carrier', description: 'Cancel RoadLink tender, book SwiftLane Expedite team-driver FTL (pickup 16:00 today, $14,600).', status: 'proposed' } },
    { type: 'action', timestamp: '2026-06-17T06:58:10Z', data: { action_type: 'notify_customer', description: 'Send Pacific Components a revised ETA (Jun 19 10:00) with tracking link. Draft prepared for account manager review.', status: 'proposed' } },
    { type: 'action', timestamp: '2026-06-17T06:58:20Z', data: { action_type: 'file_carrier_claim', description: 'File a $4,500 service-failure claim against RoadLink Freight under CA-RL-2025 §4.3.', status: 'proposed' } },
    {
      type: 'hitl_request',
      timestamp: '2026-06-17T07:00:00Z',
      data: {
        title: 'Approve $14,600 team-driver expedite for SHP-7720 (decision needed before 15:00 cutoff)',
        status: 'pending',
        approver_role: 'logistics_manager',
        options: ['approve', 'reject', 'escalate'],
      },
    },
  ],

  // ── Logistics: SLA breach risk, auto-resolved within delegated authority ──
  'SC-2024-124': [
    { type: 'case_created', timestamp: '2026-06-15T08:06:00Z', data: { status: 'detected', source: 'wms_allocation_scan' } },
    {
      type: 'agent_trace',
      timestamp: '2026-06-15T08:07:00Z',
      data: {
        agent_name: 'Supply Chain Planner',
        operation: 'plan',
        output_summary: 'Backorder approaching the contractual breach point. Plan: confirm shortfall root cause, find network inventory, check grace/penalty terms, cost the fulfillment options, decide.',
      },
    },
    {
      type: 'agent_trace',
      timestamp: '2026-06-15T08:08:00Z',
      data: {
        agent_name: 'Logistics Systems Agent',
        operation: 'get_customer_order',
        output_summary: 'SO-44187: NorthStar Logistics, 1,200 units SK-220 sensor kits, $410,000. Promise date Jun 10, now 5 days past. Ship-from Dallas DC.',
      },
    },
    {
      type: 'agent_trace',
      timestamp: '2026-06-15T08:10:00Z',
      data: {
        agent_name: 'Inventory Agent',
        operation: 'check_network_inventory',
        output_summary:
          'Root cause: Dallas cycle count on Jun 9 wrote off 640 units and the replenishment PO slipped. Available: Reno DC 900 units, Columbus DC 450 units. Combined 1,350 ≥ 1,200 needed.',
      },
    },
    {
      type: 'agent_trace',
      timestamp: '2026-06-15T08:12:00Z',
      data: {
        agent_name: 'SLA & Contract Agent',
        operation: 'rag_search',
        output_summary: 'Supply agreement SA-NS-2024 §5: 7-day grace after promise date, then 2%/day penalty ($8,200/day) and customer right to cancel. Grace ends Jun 17 23:59.',
      },
    },
    {
      type: 'evidence_collected',
      timestamp: '2026-06-15T08:12:30Z',
      data: { title: 'Grace period and cancellation clause', evidence_type: 'document', content: 'SA-NS-2024 §5.1–5.3: 7-day grace; 2%/day penalty after grace; cancellation right at day 10.' },
    },
    {
      type: 'agent_trace',
      timestamp: '2026-06-15T08:15:00Z',
      data: {
        agent_name: 'Cost & Risk Analyst',
        operation: 'compare_options',
        output_summary:
          'Ship 900 from Reno + 300 from Columbus via 2-day LTL expedite: $6,800 freight premium, delivery Jun 17 — inside grace. Waiting for Dallas replenishment (ETA Jun 21) means 4 penalty days ($32,800) and cancellation risk on $410K.',
      },
    },
    {
      type: 'agent_trace',
      timestamp: '2026-06-15T08:17:00Z',
      data: {
        agent_name: 'Decision Agent',
        operation: 'decide',
        output_summary: 'Multi-node fulfillment (confidence 0.93). $6,800 is within the $10,000 delegated freight authority, so it auto-executes. Customer notice goes to the account manager for a quick sign-off.',
      },
    },
    {
      type: 'decision',
      timestamp: '2026-06-15T08:17:30Z',
      data: { action: 'multi_node_fulfillment', decision_source: 'agent', reasoning: 'Within delegated authority; avoids $32.8K penalty and $410K cancellation risk.', owner: 'Decision Agent' },
    },
    { type: 'action', timestamp: '2026-06-15T08:20:00Z', data: { action_type: 'reallocate_inventory', description: 'Re-sourced SO-44187: 900 units from Reno DC, 300 from Columbus DC. Dallas allocation released.', status: 'executed' } },
    { type: 'action', timestamp: '2026-06-15T08:24:00Z', data: { action_type: 'book_carrier', description: 'Booked 2-day LTL expedite from Reno and Columbus ($6,800 premium).', status: 'executed' } },
    {
      type: 'hitl_request',
      timestamp: '2026-06-15T08:26:00Z',
      data: { title: 'Approve proactive revised-ETA notice to NorthStar Logistics', status: 'approved', approver_role: 'account_manager', options: ['approve', 'reject', 'escalate'] },
    },
    {
      type: 'decision',
      timestamp: '2026-06-15T09:02:00Z',
      data: { action: 'approve', decision_source: 'human', reasoning: 'Customer prefers the split delivery over waiting. Notice sent with both tracking numbers.', owner: 'Account Manager — K. Alvarez' },
    },
    { type: 'action', timestamp: '2026-06-15T09:03:00Z', data: { action_type: 'notify_customer', description: 'Revised ETA (Jun 17, two shipments) sent to NorthStar Logistics.', status: 'executed' } },
    {
      type: 'agent_trace',
      timestamp: '2026-06-16T13:05:00Z',
      data: { agent_name: 'Logistics Systems Agent', operation: 'get_shipment_status', output_summary: 'Both shipments delivered Jun 16 (Reno 11:40, Columbus 12:55). POD signed. 1 day inside grace.' },
    },
    {
      type: 'outcome',
      timestamp: '2026-06-16T13:10:00Z',
      data: {
        final_status: 'resolved',
        resolution_summary: 'Delivered inside the grace period from two alternate DCs. No penalty, order retained. Net savings vs. waiting: $26,000 plus $410K cancellation risk avoided. Root cause (Dallas cycle-count variance) routed to inventory control.',
        total_duration_hours: 29,
        human_touches: 1,
      },
    },
  ],

  // ── Procurement: short shipment, waiting on supplier within cure period ───
  'SC-2024-137': [
    { type: 'case_created', timestamp: '2026-06-15T10:21:00Z', data: { status: 'detected', source: 'erp_goods_receipt' } },
    {
      type: 'agent_trace',
      timestamp: '2026-06-15T10:22:00Z',
      data: {
        agent_name: 'Procurement Planner',
        operation: 'plan',
        output_summary: 'Inbound short receipt. Plan: reconcile PO / ASN / receipt / invoice, check production impact, review supply-agreement remedies, evaluate supplier and alternates, decide remedy and payment treatment.',
      },
    },
    {
      type: 'agent_trace',
      timestamp: '2026-06-15T10:23:00Z',
      data: {
        agent_name: 'PO Systems Agent',
        operation: 'get_po',
        output_summary: 'PO-88213: Apex Packaging, 10,000 CTN-4418 corrugated shippers at $27.50 = $275,000. Need-by Jun 13, ship-to Columbus plant.',
      },
    },
    {
      type: 'agent_trace',
      timestamp: '2026-06-15T10:24:00Z',
      data: {
        agent_name: 'PO Systems Agent',
        operation: 'get_receipts',
        output_summary: 'ASN-55102 declared 10,000 units on 8 pallets. GR-71840 counted 7,600 (6 pallets plus a partial). 2,400 units short ($66,000). No damage noted.',
      },
    },
    {
      type: 'agent_trace',
      timestamp: '2026-06-15T10:25:00Z',
      data: {
        agent_name: 'PO Systems Agent',
        operation: 'get_invoice',
        output_summary: 'INV-AP-30917 billed 10,000 units / $275,000. 3-way match fails on quantity: invoice 10,000 vs receipt 7,600.',
      },
    },
    {
      type: 'evidence_collected',
      timestamp: '2026-06-15T10:25:30Z',
      data: { title: '3-way match result', evidence_type: 'api_response', content: 'PO 10,000 / ASN 10,000 / GR 7,600 / Invoice 10,000. Quantity variance 2,400 units ($66,000).' },
    },
    {
      type: 'agent_trace',
      timestamp: '2026-06-15T10:28:00Z',
      data: {
        agent_name: 'Material Planning Agent',
        operation: 'check_coverage',
        output_summary: 'Packaging line consumes 1,900 cartons/day. On hand after receipt: 8,700 = 4.6 days of cover. Stock-out Jun 19 PM, which would block 3 outbound customer orders ($1.1M).',
      },
    },
    {
      type: 'agent_trace',
      timestamp: '2026-06-15T10:31:00Z',
      data: {
        agent_name: 'Contract Specialist',
        operation: 'rag_search',
        output_summary: 'SA-APX-2025 §6: 98% fill-rate commitment, 48h cure period for shortages, 3% shortage credit on shorted value, and buyer may cover elsewhere and recover the price difference.',
      },
    },
    {
      type: 'evidence_collected',
      timestamp: '2026-06-15T10:31:30Z',
      data: { title: 'Supply agreement remedies', evidence_type: 'document', content: 'SA-APX-2025 §6.2 (48h cure), §6.3 (3% shortage credit = $1,980), §6.4 (cover and recover cost difference).' },
    },
    {
      type: 'agent_trace',
      timestamp: '2026-06-15T10:35:00Z',
      data: {
        agent_name: 'Vendor Evaluation Analyst',
        operation: 'score_supplier',
        output_summary: 'Apex OTIF last 90 days: 91% (was 97%). 3rd short shipment in 60 days. Qualified alternate BoxCraft Industries can deliver 2,400 units in 3 days at $29.10 (+$3,840).',
      },
    },
    {
      type: 'evidence_collected',
      timestamp: '2026-06-15T10:35:30Z',
      data: { title: 'Supplier scorecard trend', evidence_type: 'precedent', content: 'Short shipments: Apr 22 (8%), May 29 (12%), Jun 15 (24%). The trend supports a corrective action request (SCAR).' },
    },
    {
      type: 'agent_trace',
      timestamp: '2026-06-15T10:40:00Z',
      data: {
        agent_name: 'Decision Agent',
        operation: 'decide',
        output_summary:
          'Recommend (confidence 0.90): (1) post partial GR, (2) pay $209K for received qty and hold $66K, (3) demand backorder within 48h cure, (4) pre-stage BoxCraft spot-buy if no confirmation by Jun 17 14:00, (5) open a SCAR. Payment hold and supplier dispute need Procurement Manager approval.',
      },
    },
    {
      type: 'hitl_request',
      timestamp: '2026-06-15T10:42:00Z',
      data: { title: 'Approve partial payment, $66K invoice hold, and backorder demand to Apex', status: 'approved', approver_role: 'procurement_manager', options: ['approve', 'reject', 'escalate'] },
    },
    {
      type: 'decision',
      timestamp: '2026-06-15T13:05:00Z',
      data: { action: 'approve', decision_source: 'human', reasoning: 'Agree with the remedy. Enforce the cure period; claim the 3% credit if backorder ships late.', owner: 'Procurement Manager — D. Okafor' },
    },
    { type: 'action', timestamp: '2026-06-15T13:08:00Z', data: { action_type: 'post_partial_receipt', description: 'GR-71840 confirmed at 7,600 units; PO line remains open for 2,400.', status: 'executed' } },
    { type: 'action', timestamp: '2026-06-15T13:10:00Z', data: { action_type: 'hold_invoice_variance', description: 'INV-AP-30917 split: $209,000 released for payment, $66,000 on quantity hold.', status: 'executed' } },
    { type: 'action', timestamp: '2026-06-15T13:14:00Z', data: { action_type: 'notify_supplier', description: 'Supplier Communication Agent sent Apex a shortage notice citing SA-APX-2025 §6.2. Backorder confirmation due Jun 17 14:00.', status: 'executed' } },
    { type: 'action', timestamp: '2026-06-15T13:20:00Z', data: { action_type: 'open_scar', description: 'Supplier corrective action request SCAR-2026-019 opened for repeat short shipments.', status: 'executed' } },
    {
      type: 'hitl_request',
      timestamp: '2026-06-17T06:30:00Z',
      data: {
        title: 'Contingency: approve BoxCraft spot-buy (2,400 units, $69,840) if Apex misses the 14:00 cure deadline',
        status: 'pending',
        approver_role: 'procurement_manager',
        options: ['approve', 'reject', 'escalate'],
      },
    },
  ],

  // ── Procurement: PO acknowledgment price mismatch ─────────────────────────
  'SC-2024-142': [
    { type: 'case_created', timestamp: '2026-06-16T09:31:00Z', data: { status: 'detected', source: 'supplier_po_ack' } },
    {
      type: 'agent_trace',
      timestamp: '2026-06-16T09:32:00Z',
      data: {
        agent_name: 'Procurement Planner',
        operation: 'plan',
        output_summary: 'Price and date mismatch on a supplier acknowledgment. Plan: compare PO vs ack, validate against contract and index, assess schedule impact, check second source, decide response.',
      },
    },
    {
      type: 'agent_trace',
      timestamp: '2026-06-16T09:33:00Z',
      data: {
        agent_name: 'PO Systems Agent',
        operation: 'get_po',
        output_summary: 'PO-88342: Meridian Metals, 18,000 kg 6063-T5 aluminum extrusion at $9.80/kg = $176,400. Need-by Jun 26.',
      },
    },
    {
      type: 'agent_trace',
      timestamp: '2026-06-16T09:34:00Z',
      data: {
        agent_name: 'PO Systems Agent',
        operation: 'get_po_acknowledgment',
        output_summary: 'Ack received Jun 16 (1 day late): $10.58/kg = $190,440 (+$14,040, +8%) labeled "energy surcharge". Promise date Jul 3 (+7 days).',
      },
    },
    {
      type: 'agent_trace',
      timestamp: '2026-06-16T09:38:00Z',
      data: {
        agent_name: 'Contract Specialist',
        operation: 'rag_search',
        output_summary: 'SA-MER-2024 §3.1: firm pricing through Dec 31 2026. §3.4: index-linked adjustment (LME aluminum) only with 60 days written notice. No notice on file.',
      },
    },
    {
      type: 'evidence_collected',
      timestamp: '2026-06-16T09:38:30Z',
      data: { title: 'Firm-price clause and notice requirement', evidence_type: 'document', content: 'SA-MER-2024 §3.1 and §3.4. Correspondence log shows no price-change notice in the last 90 days.' },
    },
    {
      type: 'agent_trace',
      timestamp: '2026-06-16T09:42:00Z',
      data: {
        agent_name: 'Cost & Risk Analyst',
        operation: 'index_check',
        output_summary: 'LME aluminum is up 2.1% over the contract window. Even an index-justified increase would be ~$0.21/kg, not $0.78/kg. The surcharge is 3.8× the index movement.',
      },
    },
    {
      type: 'agent_trace',
      timestamp: '2026-06-16T09:45:00Z',
      data: {
        agent_name: 'Material Planning Agent',
        operation: 'check_coverage',
        output_summary: 'Extrusion stock covers production through Jun 30. Jul 3 delivery leaves a 3-day gap on the Line B frame build (~$240K of finished goods delayed).',
      },
    },
    {
      type: 'agent_trace',
      timestamp: '2026-06-16T09:49:00Z',
      data: {
        agent_name: 'Vendor Evaluation Analyst',
        operation: 'second_source_check',
        output_summary: 'Northline Extrusions is qualified for 6063-T5: $10.05/kg, 14-day lead time (delivery Jun 30). Usable as negotiating leverage or a partial split.',
      },
    },
    {
      type: 'agent_trace',
      timestamp: '2026-06-16T09:54:00Z',
      data: {
        agent_name: 'Decision Agent',
        operation: 'decide',
        output_summary:
          'Recommend (confidence 0.84): reject the surcharge under §3.1/§3.4, hold the PO at $9.80/kg, and require the Jun 26 date. If Meridian cannot hold the date, split 6,000 kg to Northline. Supplier dispute plus potential re-source needs Category Manager approval.',
      },
    },
    {
      type: 'decision',
      timestamp: '2026-06-16T09:54:30Z',
      data: { action: 'reject_price_change', decision_source: 'agent', reasoning: 'Contract fixes price with no notice served; surcharge far exceeds index movement.', owner: 'Decision Agent' },
    },
    { type: 'action', timestamp: '2026-06-16T09:56:00Z', data: { action_type: 'reject_po_acknowledgment', description: 'Reject ack at $10.58/kg; reaffirm $9.80/kg and Jun 26 need-by citing SA-MER-2024 §3.1/§3.4.', status: 'proposed' } },
    { type: 'action', timestamp: '2026-06-16T09:56:10Z', data: { action_type: 'stage_split_award', description: 'Draft a 6,000 kg PO to Northline Extrusions ($60,300), released only if Meridian confirms Jul 3.', status: 'proposed' } },
    {
      type: 'hitl_request',
      timestamp: '2026-06-16T09:58:00Z',
      data: { title: 'Approve surcharge rejection and counter-notice to Meridian Metals (PO-88342)', status: 'pending', approver_role: 'category_manager', options: ['approve', 'reject', 'escalate'] },
    },
  ],
};

// ─── Entity graphs ───────────────────────────────────────────────────────────

export const scMockEntities: Record<string, { nodes: EntityNode[]; edges: EntityEdge[] }> = {
  'SC-2024-118': {
    nodes: [
      { entity_id: 'SO-44120', entity_type: 'customer_order', display_name: 'Order SO-44120 ($820K)' },
      { entity_id: 'CUST-PC', entity_type: 'customer', display_name: 'Pacific Components (Fremont plant)' },
      { entity_id: 'SHP-7720', entity_type: 'shipment', display_name: 'Shipment SHP-7720 (3 missed pickups)' },
      { entity_id: 'DC-MEM', entity_type: 'warehouse', display_name: 'Memphis DC (dock 14)' },
      { entity_id: 'CARRIER-RL', entity_type: 'carrier', display_name: 'RoadLink Freight (failed)' },
      { entity_id: 'CARRIER-SWL', entity_type: 'carrier', display_name: 'SwiftLane Expedite (recommended)' },
      { entity_id: 'MSA-PC-2023', entity_type: 'contract', display_name: 'MSA-PC-2023 (§7.2 penalty, §7.5 line-down)' },
      { entity_id: 'DECISION-118', entity_type: 'decision', display_name: 'Decision: team-driver expedite (pending approval)' },
    ],
    edges: [
      { source: 'SO-44120', target: 'CUST-PC', relationship: 'ordered_by' },
      { source: 'SHP-7720', target: 'SO-44120', relationship: 'fulfills' },
      { source: 'SHP-7720', target: 'DC-MEM', relationship: 'ships_from' },
      { source: 'SHP-7720', target: 'CARRIER-RL', relationship: 'tendered_to' },
      { source: 'CARRIER-SWL', target: 'SHP-7720', relationship: 'alternate_for' },
      { source: 'SO-44120', target: 'MSA-PC-2023', relationship: 'governed_by' },
      { source: 'DECISION-118', target: 'CARRIER-SWL', relationship: 'selects' },
      { source: 'DECISION-118', target: 'MSA-PC-2023', relationship: 'considered_clause' },
    ],
  },
  'SC-2024-124': {
    nodes: [
      { entity_id: 'SO-44187', entity_type: 'customer_order', display_name: 'Order SO-44187 ($410K)' },
      { entity_id: 'CUST-NS', entity_type: 'customer', display_name: 'NorthStar Logistics' },
      { entity_id: 'SKU-SK220', entity_type: 'item', display_name: 'SK-220 sensor kit (1,200 units)' },
      { entity_id: 'DC-DAL', entity_type: 'warehouse', display_name: 'Dallas DC (short 640 after cycle count)' },
      { entity_id: 'DC-RNO', entity_type: 'warehouse', display_name: 'Reno DC (shipped 900)' },
      { entity_id: 'DC-CMH', entity_type: 'warehouse', display_name: 'Columbus DC (shipped 300)' },
      { entity_id: 'SA-NS-2024', entity_type: 'contract', display_name: 'SA-NS-2024 (7-day grace)' },
      { entity_id: 'DECISION-124', entity_type: 'decision', display_name: 'Decision: multi-node fulfillment (auto)' },
    ],
    edges: [
      { source: 'SO-44187', target: 'CUST-NS', relationship: 'ordered_by' },
      { source: 'SO-44187', target: 'SKU-SK220', relationship: 'contains' },
      { source: 'SO-44187', target: 'DC-DAL', relationship: 'originally_allocated_to' },
      { source: 'DC-RNO', target: 'SO-44187', relationship: 'fulfilled' },
      { source: 'DC-CMH', target: 'SO-44187', relationship: 'fulfilled' },
      { source: 'SO-44187', target: 'SA-NS-2024', relationship: 'governed_by' },
      { source: 'DECISION-124', target: 'SO-44187', relationship: 'decided_on' },
    ],
  },
  'SC-2024-137': {
    nodes: [
      { entity_id: 'PO-88213', entity_type: 'purchase_order', display_name: 'PO-88213 (10,000 cartons, $275K)' },
      { entity_id: 'SUP-APX', entity_type: 'supplier', display_name: 'Apex Packaging (OTIF 91%)' },
      { entity_id: 'ASN-55102', entity_type: 'asn', display_name: 'ASN-55102 (declared 10,000)' },
      { entity_id: 'GR-71840', entity_type: 'goods_receipt', display_name: 'GR-71840 (received 7,600)' },
      { entity_id: 'INV-AP-30917', entity_type: 'invoice', display_name: 'INV-AP-30917 ($66K on hold)' },
      { entity_id: 'SA-APX-2025', entity_type: 'contract', display_name: 'SA-APX-2025 (48h cure, 3% credit)' },
      { entity_id: 'SUP-BXC', entity_type: 'supplier', display_name: 'BoxCraft Industries (alternate)' },
      { entity_id: 'SCAR-2026-019', entity_type: 'corrective_action', display_name: 'SCAR-2026-019' },
    ],
    edges: [
      { source: 'PO-88213', target: 'SUP-APX', relationship: 'issued_to' },
      { source: 'ASN-55102', target: 'PO-88213', relationship: 'advises' },
      { source: 'GR-71840', target: 'PO-88213', relationship: 'receives_against' },
      { source: 'INV-AP-30917', target: 'PO-88213', relationship: 'bills_against' },
      { source: 'PO-88213', target: 'SA-APX-2025', relationship: 'governed_by' },
      { source: 'SUP-BXC', target: 'PO-88213', relationship: 'contingency_for' },
      { source: 'SCAR-2026-019', target: 'SUP-APX', relationship: 'raised_against' },
    ],
  },
  'SC-2024-142': {
    nodes: [
      { entity_id: 'PO-88342', entity_type: 'purchase_order', display_name: 'PO-88342 (18,000 kg @ $9.80)' },
      { entity_id: 'ACK-88342', entity_type: 'po_acknowledgment', display_name: 'PO Ack (@ $10.58, Jul 3)' },
      { entity_id: 'SUP-MER', entity_type: 'supplier', display_name: 'Meridian Metals' },
      { entity_id: 'SA-MER-2024', entity_type: 'contract', display_name: 'SA-MER-2024 (firm price to Dec 2026)' },
      { entity_id: 'IDX-LME-AL', entity_type: 'market_index', display_name: 'LME Aluminum (+2.1%)' },
      { entity_id: 'SUP-NLX', entity_type: 'supplier', display_name: 'Northline Extrusions (2nd source)' },
      { entity_id: 'DECISION-142', entity_type: 'decision', display_name: 'Decision: reject surcharge (pending approval)' },
    ],
    edges: [
      { source: 'PO-88342', target: 'SUP-MER', relationship: 'issued_to' },
      { source: 'ACK-88342', target: 'PO-88342', relationship: 'acknowledges' },
      { source: 'PO-88342', target: 'SA-MER-2024', relationship: 'governed_by' },
      { source: 'SA-MER-2024', target: 'IDX-LME-AL', relationship: 'indexed_to' },
      { source: 'SUP-NLX', target: 'PO-88342', relationship: 'alternate_for' },
      { source: 'DECISION-142', target: 'ACK-88342', relationship: 'rejects' },
      { source: 'DECISION-142', target: 'SA-MER-2024', relationship: 'considered_clause' },
    ],
  },
};
